/**
 * Exposes live (pending/claimed) master-solution / grading-scheme job rows to the client — the single
 * source the ms-gs canvas (and the read-only GS echo in the grading view) derives its per-box loading
 * spinner from, covering all three producers uniformly:
 *
 * - `msExtraction`/`gsExtraction`: one row per EXAM (a whole-document bbx+OCR pass) — there is no way to
 *   know in advance which nodes it'll fill, so a live row means "every box of this artifact is in
 *   flight." This isn't a simplification for convenience: verified against the actual extraction code,
 *   completion genuinely lands within one short window regardless (one bbx call for the whole document,
 *   OCR calls fan out via an unbatched Promise.all, and even the per-node "done" mark is written once per
 *   ROOT in bulk) — so a coarse, whole-artifact spinner is not observably different from a precise one.
 * - `msGeneration`/`gsGeneration`: one row per ROOT, but its payload carries `targetTaskIds` — the exact
 *   gradable-node ids that root's call targets — computed once at dispatch (see ms-generation.job.ts /
 *   gs-generation.job.ts), so the spinner is precise per node despite the job being per root.
 * - `msRefine`/`gsRefine`: one row per refine request, carrying the exact `taskRefs` requested and
 *   (`gsRefine` only) `target` ('content' vs 'languageUse') — the one piece of information
 *   `SolutionProgress` structurally cannot express, since it's one shared record per artifact with no
 *   dimension field.
 *
 * `SolutionProgress` (extraction-progress.ts) is unaffected by any of this — it still drives the "X of Y"
 * progress bar exactly as before. This endpoint is spinner-only.
 */
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { formatTaskReferenceForDisplay } from '../../../shared/grammar-rules-resolution.js';
import { queryLiveJobRows, watchLiveJobRows } from './live-job-rows.js';

const LIVE_SOLUTION_JOB_TYPES = [
  'msExtraction',
  'msGeneration',
  'msRefine',
  'gsExtraction',
  'gsGeneration',
  'gsRefine',
] as const;

export interface LiveSolutionJobRow {
  type: (typeof LIVE_SOLUTION_JOB_TYPES)[number];
  /** `msGeneration`/`gsGeneration` only — the exact gradable-node ids (getTaskKey) this root's call
   * targets, precomputed at dispatch (see ms-generation.job.ts / gs-generation.job.ts). */
  targetTaskIds?: string[];
  /** `msRefine`/`gsRefine` only — the requested task refs, dot-formatted so they compare directly
   * against `task.taskNumber` / a qualified path once normalized the same way. */
  taskRefs?: string[];
  /** `gsRefine` only — 'content' (default/absent) or 'languageUse'. */
  target?: string;
}

function toRow(doc: any): LiveSolutionJobRow | null {
  const type = doc?.type as LiveSolutionJobRow['type'] | undefined;
  if (!type || !(LIVE_SOLUTION_JOB_TYPES as readonly string[]).includes(type)) return null;
  const payload = doc?.payload ?? {};
  return {
    type,
    ...(Array.isArray(payload.targetTaskIds) ? { targetTaskIds: payload.targetTaskIds.map(String) } : {}),
    ...(Array.isArray(payload.taskRefs)
      ? { taskRefs: payload.taskRefs.map((ref: unknown) => formatTaskReferenceForDisplay(String(ref))) }
      : {}),
    ...(payload.target ? { target: String(payload.target) } : {}),
  };
}

export const getLiveSolutionJobsQuery = registerQuery({
  operationName: 'getLiveSolutionJobsQuery',
  type: () => [String],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async () => queryLiveJobRows(examId, LIVE_SOLUTION_JOB_TYPES, toRow),
});

export const streamLiveSolutionJobsByExamIdSubscription = registerSubscription({
  operationName: 'streamLiveSolutionJobsByExamIdSubscription',
  type: () => [String],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => () => watchLiveJobRows(examId, LIVE_SOLUTION_JOB_TYPES, toRow),
});
